import { Form, Select, Tag } from 'antd';
import { FormInstance } from 'antd/es/form';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import { TPODataContext } from 'contexts/TPOContext';
import _ from 'lodash';
import { Dispatch, FC, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import { useFetchAttributesList } from 'hooks/tradePromoOptimization/events/useFetchAttributesList';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig, sendNotification } from 'utils/helperMethods';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { AttributesPromoMapping } from 'types/tradePromoOptimization';
import { TPO_UI_LIMITS } from 'constants/tpo';

type Props = {
    open: boolean;
    form: FormInstance<any>;
    eventId: string | null;
    disabledEvent: boolean;
    nestedAttributePromoMapping: AttributesPromoMapping;
};

export const selectAllObject = { label: 'Select All', value: 'select_all' };
const enableSelectAll = true; //move to config later

const PPGSelection: FC<Props> = ({ open, form, eventId, disabledEvent, nestedAttributePromoMapping }) => {
    //search state
    const [dim1SearchValue, setDim1SearchValue] = useState('');
    const [dim1NestedSearchValue, setDim1NestedSearchValue] = useState('');
    const [dim1NestedVariables, setDim1NestedVariables] = useState<any>({});
    const [dim1Variables, setDim1Variables] = useState({});

    //context
    const { dimensionConfigData, selectedPlanInfo, selectedEventInfo } = useContext(TPODataContext);
    const { appConfig } = useContext(UserContext);
    const tradePromoOptimizationConfig = getConfig('tradePromoOptimization', appConfig);
    const limit = tradePromoOptimizationConfig?.attributesListLimit;
    const prevDim1NestedList = useRef<any[]>([]);
    function handleSearch(val: string, from: 'parent' | 'nested') {
        if (from === 'parent') {
            setDim1Variables({ ...dim1Variables, offset: 0, search_string: val });
        } else {
            setDim1NestedVariables({ ...dim1NestedVariables, offset: 0, search_string: val });
        }
    }

    //debounced search handler
    const debouncedHandleSearch = useCallback(_.debounce(handleSearch, 400), [dim1Variables, dim1NestedVariables]);

    //this is used to re render the form
    //by default antd forms do not cause a re render
    const selectedDim1Values = Form.useWatch('selectedDim1Values', form) ?? [];
    const selectedDim1NestedValues = Form.useWatch('selectedDim1NestedValues', form) ?? [];

    //react query to fetch list - dim1 parent - brand
    const {
        data: dim1List,
        loading: dim1ListLoading,
        error: dim1ListError,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: open && !_.isEmpty(dim1Variables) },
        queryKey: ['tpo-events-get-attribute-types', 'tpo-events-get-attribute-types-dim1', dim1Variables],
        url: `${tradePromoOptimizationUrls.getAttributeListUrl}`,
        variables: { ...dim1Variables },
        parserFunction: (res: any) => {
            if (res?.data?.length === 0) {
                throw 'No Data';
            }
            if (Array.isArray(res?.data) && res.data.length > 0) {
                const tickedAttributes: any[] = [...(form.getFieldValue('selectedDim1Values') ?? [])]; //preserve user selection
                const result = res?.data?.map((item: any) => {
                    if (item?.ticked) {
                        tickedAttributes.push({ label: item?.label, value: item?.id ?? item?.label });
                    }
                    return {
                        label: item?.label,
                        value: item?.id ?? item?.label,
                    };
                });

                form.setFieldValue('selectedDim1Values', tickedAttributes);
                return result;
            }
            return [];
        },
    });

    //react query to fetch list - dim1 nested - product
    const {
        data: dim1NestedList,
        loading: dim1NestedListLoading,
        error: dim1NestedListError,
        count: dim1NestedListCount,
    } = useFetchAttributesList({
        method: 'post',
        queryConfig: {
            enabled: open && eventId ? !_.isEmpty(dim1NestedVariables) : Boolean(dim1NestedVariables?.filters),
        },
        queryKey: ['tpo-events-get-attribute-types', 'tpo-events-get-attribute-types-dim1-nested', dim1NestedVariables],
        url: `${tradePromoOptimizationUrls.getAttributeListUrl}`,
        variables: { ...dim1NestedVariables },
        parserFunction: (res: any) => {
            const tickedAttributes: any[] = [...(form.getFieldValue('selectedDim1NestedValues') ?? [])]; //preserve user selection
            const result = res?.map((item: any) => {
                if (item?.ticked) {
                    tickedAttributes.push({ label: item?.label, value: item?.id ?? item?.label });
                }
                return {
                    label: item?.label,
                    value: item?.id ?? item?.label,
                };
            });

            //adding select all object
            if (enableSelectAll && dim1NestedVariables?.offset === 0) {
                result?.unshift(selectAllObject);
                //if all are selected by default in edit mode, then add select all to ticked
                if (tickedAttributes?.length === result?.length - 1) {
                    tickedAttributes.unshift(selectAllObject);
                }
            }
            form.setFieldValue('selectedDim1NestedValues', tickedAttributes);
            return result;
        },
    });

    //remove select all
    const updatedDim1NestedListValues = useMemo(() => {
        if (enableSelectAll) {
            return selectedDim1NestedValues?.filter((item: any) => item?.value !== selectAllObject?.value);
        }
        return selectedDim1NestedValues;
    }, [selectedDim1NestedValues]);

    //if dim1 nested exists, then display that, else dim1
    const selectedItemsToDisplay = useMemo(() => {
        return dimensionConfigData?.eventDim1NestedAttribute?.key ? updatedDim1NestedListValues : selectedDim1Values;
    }, [dimensionConfigData?.eventDim1NestedAttribute?.key, updatedDim1NestedListValues, selectedDim1Values]);

    //creating filter obj of plan
    const planFilter = useMemo(() => {
        let filterObj: { inclusion: any[]; exclusion: any[] } = {
            inclusion: [],
            exclusion: [],
        };
        selectedPlanInfo?.dim1Filter?.value?.forEach((item) => {
            filterObj.inclusion.push({
                attributeType: dimensionConfigData?.dim1Attribute?.key,
                attributeDesc: item,
                attributeExId: item,
                dimension: dimensionConfigData?.dim1Attribute?.dimension,
            });
        });
        selectedPlanInfo?.dim2Filter?.value?.forEach((item) => {
            filterObj.inclusion.push({
                attributeType: dimensionConfigData?.dim2Attribute?.key,
                attributeDesc: item,
                attributeExId: item,
                dimension: dimensionConfigData?.dim2Attribute?.dimension,
            });
        });
        return filterObj;
    }, [selectedPlanInfo?.dim1Filter]);

    useEffect(() => {
        if (open) {
            //setting dropdown variables so that api call is triggered
            setDim1Variables({
                // event_id: eventId ?? null,
                search_string: dim1SearchValue,
                attribute_type: dimensionConfigData?.eventDim1Attribute?.key,
                filters: planFilter,
                limit: limit,
                offset: 0,
                lowest_attribute: false,
                plan_year: selectedPlanInfo?.planYear,
                dimension: dimensionConfigData?.eventDim1Attribute?.dimension,
            });

            if (dimensionConfigData?.eventDim1NestedAttribute?.key) {
                setDim1NestedVariables({
                    // event_id: null,
                    search_string: dim1NestedSearchValue,
                    attribute_type: dimensionConfigData?.eventDim1NestedAttribute?.key,
                    filters: eventId ? planFilter : null, //disable initial api call in add event flow for lowest level
                    limit: limit,
                    lowest_attribute: true,
                    offset: 0,
                    plan_year: selectedPlanInfo?.planYear,
                    dimension: dimensionConfigData?.eventDim1NestedAttribute?.dimension,
                });
            }
        }

        return () => {
            //reset search on popup opening
            setDim1SearchValue('');
            setDim1NestedSearchValue('');
        };
    }, [open]);

    //throw error if more than 20  products are there
    function validateItemsLength(value: string[], label: string) {
        if (value && value.length > TPO_UI_LIMITS.PPG_SELECTION) {
            return Promise.reject(new Error(`You can select up to ${TPO_UI_LIMITS.PPG_SELECTION} ${label}.`));
        }
        return Promise.resolve();
    }

    //list inf scroller function for dim1 nested - material group/product
    const onInfiniteScrollOnNextMemoised = useCallback(() => {
        if (dim1NestedListLoading) return;
        const newOffset = dim1NestedVariables?.offset + dim1NestedVariables?.limit;
        setDim1NestedVariables({ ...dim1NestedVariables, offset: newOffset });
    }, [dim1NestedVariables, dim1NestedListLoading]);

    return (
        <div className="w-full flex flex-col gap-3 border border-backgroundGreyAlt6 rounded p-3 bg-white">
            {/* dim selection component */}
            <div className="flex flex-col gap-2.5 bg-backgroundGreyAlt6 p-3 pr-4 rounded">
                <Form
                    disabled={disabledEvent}
                    name="product-selection"
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    form={form}
                >
                    <Form.Item
                        label={`${dimensionConfigData?.eventDim1Attribute?.label} Selection`}
                        name="selectedDim1Values"
                        rules={[
                            {
                                required: true,
                                message: `Please select at-least 1 ${dimensionConfigData?.eventDim1Attribute?.label}`,
                            },
                            {
                                validator: (_, value: string[]) => {
                                    return validateItemsLength(
                                        value,
                                        dimensionConfigData?.eventDim1Attribute?.label ?? '',
                                    );
                                },
                            },
                        ]}
                    >
                        <SelectWithInfiniteScroller
                            data-testid="tpo_event_dim1_attribute_select"
                            hasMore={false}
                            mode="multiple"
                            labelInValue
                            showSearch
                            onSearch={(val) => {
                                setDim1SearchValue(val);
                                debouncedHandleSearch(val, 'parent');
                            }}
                            notFoundContent={
                                dim1ListLoading ? (
                                    <div className="p-3">
                                        <Loader />{' '}
                                    </div>
                                ) : (
                                    <ErrorBox msg="No Data" />
                                )
                            } // Show loader
                            onVisibilityChange={(open: boolean) => {
                                //when popup is closed, create filter object for nested attr type
                                if (!open) {
                                    const selectedDim1Values = form.getFieldValue('selectedDim1Values');
                                    if (selectedDim1Values?.length > 0) {
                                        const inclusionArr = selectedDim1Values?.map((item: any) => {
                                            return {
                                                attributeType: dimensionConfigData?.eventDim1Attribute?.key,
                                                attributeDesc: item?.label,
                                                attributeExId: item?.label,
                                                dimension: dimensionConfigData?.eventDim1Attribute?.dimension,
                                            };
                                        });
                                        const filters = {
                                            inclusion: [...planFilter?.inclusion, ...inclusionArr],
                                            exclusion: [],
                                        };
                                        setDim1NestedVariables({ ...dim1NestedVariables, filters, offset: 0 });
                                    }
                                }
                            }}
                            loading={dim1ListLoading}
                            filterOption={false}
                            placeholder={`Select ${dimensionConfigData?.eventDim1Attribute?.label}`}
                            searchValue={dim1SearchValue}
                            maxTagCount="responsive"
                            className="dropdown-height  w-7/10 "
                            showArrow
                            options={dim1List}
                            tagRender={(props) => {
                                return <Tag style={{ marginRight: 4 }}>{props?.label}</Tag>;
                            }}
                        />
                    </Form.Item>
                    {dimensionConfigData?.eventDim1NestedAttribute?.key && (
                        <Form.Item
                            label={`${dimensionConfigData?.eventDim1NestedAttribute?.label} Selection`}
                            name="selectedDim1NestedValues"
                            rules={[
                                {
                                    required: true,
                                    message: `Please select at-least 1 ${dimensionConfigData?.eventDim1NestedAttribute?.label}`,
                                },
                                {
                                    validator: (_, value: string[]) => {
                                        return validateItemsLength(
                                            value,
                                            dimensionConfigData?.eventDim1NestedAttribute?.label ?? '',
                                        );
                                    },
                                },
                            ]}
                        >
                            <SelectWithInfiniteScroller
                                data-testid="tpo_event_dim1_nested_attribute_select"
                                hasMore={dim1NestedList?.length < dim1NestedListCount}
                                onInfiniteScrollOnNextMemoised={onInfiniteScrollOnNextMemoised}
                                mode="multiple"
                                labelInValue
                                filterOption={false}
                                showSearch
                                tagRender={(props) => {
                                    if (props.value === selectAllObject?.value) {
                                        return <></>; // don't render tag for 'ALL'
                                    }
                                    return <Tag style={{ marginRight: 4 }}>{props?.label}</Tag>;
                                }}
                                onChange={(value, options) => {
                                    const isSelectAllSelected = value?.find(
                                        (item: any) => item?.value === selectAllObject?.value,
                                    );
                                    const isSelectAllPrevSelected = selectedDim1NestedValues?.find(
                                        (item: any) => item?.value === selectAllObject?.value,
                                    );
                                    //if user is unselecting select all then unselect everything
                                    if (isSelectAllPrevSelected && !isSelectAllSelected) {
                                        form.setFieldValue('selectedDim1NestedValues', []);
                                    }
                                    //if user clicks on select all, then select all values
                                    if (!isSelectAllPrevSelected && isSelectAllSelected) {
                                        form.setFieldValue('selectedDim1NestedValues', dim1NestedList);
                                    }
                                    return;
                                }}
                                onSearch={(val) => {
                                    setDim1NestedSearchValue(val);
                                    debouncedHandleSearch(val, 'nested');
                                }}
                                notFoundContent={
                                    dim1NestedListLoading ? (
                                        <div className="p-3">
                                            <Loader />{' '}
                                        </div>
                                    ) : (
                                        <ErrorBox msg="No Data" />
                                    )
                                } // Show loader                                filterOption={false}
                                placeholder={`Select ${dimensionConfigData?.eventDim1NestedAttribute?.label}`}
                                searchValue={dim1NestedSearchValue}
                                maxTagCount="responsive"
                                className="dropdown-height  w-7/10 "
                                showArrow
                                // onSearch={(val) => {
                                //     setDim1NestedSearchValue(val);
                                //     debouncedHandleSearch(val);
                                // }}
                                disabled={disabledEvent}
                                loading={dim1NestedListLoading}
                                options={dim1NestedList}
                            />
                        </Form.Item>
                    )}
                </Form>
            </div>

            <div className=" flex-grow bg-backgroundGreyAlt6 p-3 rounded overflow-y-auto ">
                {selectedItemsToDisplay.map((item: any) => {
                    return (
                        <div
                            key={item?.label}
                            className={`flex justify-between items-center bg-white rounded py-2 pl-4 pr-2.5 mb-2.5 ${
                                !disabledEvent ? '' : '!bg-promoColour opacity-50 cursor-not-allowed'
                            }`}
                        >
                            <span>{item?.label}</span>
                            <span
                                onClick={() => {
                                    if (disabledEvent) return;
                                    // checking if dim1 nested is available
                                    if (dimensionConfigData?.eventDim1NestedAttribute?.key) {
                                        if (
                                            nestedAttributePromoMapping &&
                                            nestedAttributePromoMapping?.[item?.value]?.length > 0
                                        ) {
                                            return sendNotification(
                                                'info',
                                                `${nestedAttributePromoMapping?.[item?.value]?.join(
                                                    ', ',
                                                )} promo are present`,
                                            );
                                        }
                                        form.setFieldValue(
                                            'selectedDim1NestedValues',
                                            selectedDim1NestedValues?.filter(
                                                (attr: any) => attr?.label !== item?.label,
                                            ),
                                        );
                                    } else {
                                        if (
                                            nestedAttributePromoMapping &&
                                            nestedAttributePromoMapping?.[item?.value]?.length > 0
                                        ) {
                                            return sendNotification(
                                                'info',
                                                `${nestedAttributePromoMapping?.[item?.value]?.join(
                                                    ', ',
                                                )} promo are present`,
                                            );
                                        }
                                        form.setFieldValue(
                                            'selectedDim1Values',
                                            selectedDim1Values?.filter((attr: any) => attr?.label !== item?.label),
                                        );
                                    }
                                }}
                                className={`${!disabledEvent ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                            >
                                <CloseOutlined />
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PPGSelection;
